// Infection control (docs/plans/phase3-operations-modules.md §2.3). URLs carry internal IDs
// and dates only; notes and organisms travel in bodies.
import { queryOptions } from '@tanstack/react-query';
import {
  infectionLineListSchema,
  infectionOptionsSchema,
  infectionStaysSchema,
  isolationFlagListSchema,
  operationsListItemSchema,
  type RecordInfectionEventRequest,
  type SaveOperationsListRequest,
  type StartIsolationFlagRequest,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

export const infectionKeys = {
  all: ['infection'] as const,
  options: ['infection', 'options'] as const,
  stays: ['infection', 'stays'] as const,
  flags: (patientId: string) => ['infection', 'flags', patientId] as const,
  lineList: (from: string, to: string) => ['infection', 'line-list', from, to] as const,
};

export function fetchInfectionOptions() {
  return api('GET', '/infection-control/options', { schema: infectionOptionsSchema });
}

export const infectionOptionsQuery = queryOptions({
  queryKey: infectionKeys.options,
  queryFn: fetchInfectionOptions,
});

export const infectionStaysQuery = queryOptions({
  queryKey: infectionKeys.stays,
  queryFn: () => api('GET', '/infection-control/stays', { schema: infectionStaysSchema }),
});

export function isolationFlagsQuery(patientId: string) {
  return queryOptions({
    queryKey: infectionKeys.flags(patientId),
    queryFn: () =>
      api('GET', `/patients/${patientId}/isolation-flags`, { schema: isolationFlagListSchema }),
  });
}

export function lineListQuery(from: string, to: string) {
  return queryOptions({
    queryKey: infectionKeys.lineList(from, to),
    queryFn: () =>
      api('GET', `/infection-control/line-list?from=${from}&to=${to}`, {
        schema: infectionLineListSchema,
      }),
  });
}

export function startIsolationFlag(patientId: string, body: StartIsolationFlagRequest) {
  return api('POST', `/patients/${patientId}/isolation-flags`, {
    body,
    schema: isolationFlagListSchema,
  });
}

export function endIsolationFlag(flagId: string, reason: string) {
  return api('POST', `/isolation-flags/${flagId}/end`, {
    body: { reason },
    schema: isolationFlagListSchema,
  });
}

export function isolationFlagEnteredInError(flagId: string, reason: string) {
  return api('POST', `/isolation-flags/${flagId}/entered-in-error`, {
    body: { reason },
    schema: isolationFlagListSchema,
  });
}

export function recordInfectionEvent(admissionId: string, body: RecordInfectionEventRequest) {
  return api('POST', `/admissions/${admissionId}/infection-events`, {
    body,
    schema: z.object({ id: z.uuid() }),
  });
}

export function infectionEventEnteredInError(eventId: string, reason: string) {
  return api('POST', `/infection-events/${eventId}/entered-in-error`, { body: { reason } });
}

export function saveInfectionList(
  kind: 'isolation_precaution' | 'infection_category',
  body: SaveOperationsListRequest,
) {
  return api('PUT', `/infection-control/options/${kind}`, {
    body,
    schema: z.array(operationsListItemSchema),
  });
}
